import {
  Table as AntTable,
  TablePaginationConfig,
  TableProps,
  TableProps as _TableProps,
} from 'antd'
import { ColumnType } from 'antd/es/table'
import { Icon } from 'shared/iconpack'
import clsx from 'shared/libs/clsx'
import { useAppSearchParams } from 'shared/libs/hooks/useAppSearchParams'
import { useOrderingTable } from 'shared/libs/hooks/useOrderingTable/useOrderingTable'
import { paginationDefault } from 'shared/model/paginationDefault'
import { ColumnTypeTable } from 'shared/types/Common'
import { ErrorResponse } from 'shared/types/ErrorResponse'
import { ParamKey } from 'shared/types/QueryModel'

import { FC, useEffect, useMemo, useState } from 'react'

import EmptyTable from 'shared/ui/Table/EmptyTable'

import { Button } from '../Button'
import ErrorInfo from '../ErrorInfo'

import CellWrapper, { CellWrapperProps } from './CellWrapper'
import { renderCell } from './CellWrapper/renderCell'
import './Table.css'

const pageSizeOptions = [10, 20, 30]

interface AppTableProps<RecordType> extends _TableProps<any> {
  error?: ErrorResponse
  columns: ColumnType<RecordType>[]
  total: number
  filterParams?: ParamKey[]
}

export function Table<RecordType = any>({
  onRow,
  columns,
  error,
  pagination,
  dataSource,
  components,
  filterParams = [],
  total = 0,
  className,
  ...restProps
}: AppTableProps<RecordType>) {
  const { page, limit } = paginationDefault
  const { getParam } = useAppSearchParams()
  const [expandedRows, setExpandedRows] = useState<any[]>([])

  const ordering = getParam('ordering')?.split(',') || []
  const columnsWithSortOrder = (columns as ColumnTypeTable<any>[])?.map(
    (col) => {
      const field = col.sortField || (col.dataIndex as string)
      const order = ordering.includes(field)
        ? 'descend'
        : ordering.includes(`-${field}`)
          ? 'ascend'
          : null

      return {
        ...col,
        sortOrder: order,
      }
    },
  )

  const columnsWrapper = columnsWithSortOrder?.map(renderCell)

  const { addCommonParmas } = useOrderingTable()

  const isResWithFilters = useMemo(() => {
    return filterParams.some((param) => (getParam(param) ? true : false))
  }, [total, filterParams])

  const initialPage = Number(getParam('page') || page)
  const initialPageSize = Number(getParam('limit') || limit)

  const [internalPagination, setInternalPagination] =
    useState<TablePaginationConfig>({
      ...pagination,
      pageSizeOptions,
      showSizeChanger: true,
      locale: {
        items_per_page: '',
      },
      position: ['bottomCenter'],
      current: initialPage,
      pageSize: initialPageSize,
      total,
    })

  useEffect(() => {
    setInternalPagination((prev) => ({
      ...prev,
      total,
      current: initialPage,
      pageSize: initialPageSize,
    }))
  }, [total, initialPage, initialPageSize])

  if (error) {
    return <ErrorInfo error={error} />
  }

  const onTableChange: TableProps['onChange'] = (
    pagination,
    _filters,
    sorter,
    _extra,
  ) => {
    setInternalPagination({
      ...pagination,
      current: pagination.current,
      pageSize: pagination.pageSize,
    })
    addCommonParmas(pagination, sorter)
  }

  return (
    <AntTable
      className={`${className} wrapper`}
      columns={columnsWrapper}
      dataSource={dataSource}
      components={
        dataSource && dataSource.length
          ? {
              body: {
                ...components?.body,

                cell: CellWrapper as FC<CellWrapperProps>,
              },
            }
          : undefined
      }
      expandIcon={({ record, onExpand, expanded }) => (
        <Button
          className={clsx('p-[0px]', '!w-[24px]')}
          variant="outlined"
          color="primary"
          size="small"
          onClick={(e) => {
            onExpand(record, e)
            setExpandedRows(
              expandedRows.includes(record.id)
                ? expandedRows.filter((id) => id !== record.id)
                : [...expandedRows, record.id],
            )
          }}
        >
          <Icon
            className={clsx('transition-all', expanded && 'rotate-180')}
            size={16}
            name="arrowDown"
          />
        </Button>
      )}
      locale={{
        emptyText: <EmptyTable isWithFilters={isResWithFilters} />,
      }}
      rowKey={(record) => (record?.id ? record.id : Math.random())}
      pagination={pagination === false ? false : internalPagination}
      onRow={onRow}
      onChange={onTableChange}
      {...restProps}
    />
  )
}
